// Engagements list — server-side search/sort/filter + infinite pagination.
// Search hits name + client + targets in SQL; superusers get an org
// dropdown to scope to one tenant ("company filter").

import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { Empty } from "@/components/ui/Empty";
import { Input } from "@/components/ui/Input";
import { Loading } from "@/components/ui/Loading";
import { PageHeader } from "@/components/ui/PageHeader";
import { usePlanUsage } from "@/hooks/useDashboard";
import { useAdminOrgs } from "@/hooks/useAdmin";
import {
  type EngagementsFilters,
  useEngagements,
} from "@/hooks/useEngagements";
import { useDict, fmt } from "@/i18n/LocaleProvider";
import { ApiError } from "@/lib/api";
import { useAuthStore } from "@/stores/authStore";
import { getStatusBadgeColors } from "@/types/engagement";

type SortKey = "newest" | "oldest" | "name" | "status";

const SORT_PARAMS: Record<SortKey, Pick<EngagementsFilters, "sort_by" | "order">> = {
  newest: {},
  oldest: { sort_by: "created_at", order: "asc" },
  name:   { sort_by: "name", order: "asc" },
  status: { sort_by: "status", order: "asc" },
};

const STATUS_OPTIONS = ["all", "draft", "scanning", "completed", "archived"];

export function EngagementsPage() {
  const navigate = useNavigate();
  const t = useDict().app.engagements;
  const isSuperuser = useAuthStore((s) => s.user?.is_superuser ?? false);

  const [q, setQ] = useState("");
  const [status, setStatus] = useState<string>("all");
  const [sort, setSort] = useState<SortKey>("newest");
  const [orgId, setOrgId] = useState<string>("all");

  const filters: EngagementsFilters = {
    q: q.trim() || undefined,
    status: status === "all" ? undefined : status,
    org_id: isSuperuser && orgId !== "all" ? orgId : undefined,
    ...SORT_PARAMS[sort],
  };

  const {
    data, isLoading, isError, error,
    fetchNextPage, hasNextPage, isFetchingNextPage,
  } = useEngagements(filters);

  const usage = usePlanUsage();
  const atEngagementCap =
    !!usage.data &&
    usage.data.max_engagements > 0 &&
    usage.data.engagements_total >= usage.data.max_engagements;

  // Superuser-only: list of all orgs for the company filter dropdown.
  // The admin endpoint already paginates; one page of 500 covers any
  // realistic deployment right now (cap = 500).
  const orgsList = useAdminOrgs({ limit: 500, offset: 0 });

  if (isLoading) return <Loading text={t.loading} />;

  if (isError) {
    const msg =
      error instanceof ApiError
        ? String(error.detail)
        : error instanceof Error
          ? error.message
          : t.unknownError;
    return (
      <Card className="border-danger">
        <span className="text-danger font-mono text-[13px]">{t.error} {msg}</span>
      </Card>
    );
  }

  const engagements = data?.pages.flatMap((p) => p.engagements) ?? [];
  const total = data?.pages[0]?.total ?? 0;
  const running = engagements.filter((e) => e.status === "scanning").length;
  const subtitle = fmt(t.subtitle, { total, running });

  return (
    <div>
      <PageHeader
        title={t.title}
        subtitle={subtitle}
        actions={
          <Button
            variant="primary"
            disabled={atEngagementCap}
            onClick={() => navigate("/app/engagements/new")}
            title={
              atEngagementCap
                ? fmt(t.planLimit, { used: usage.data?.engagements_total ?? 0, max: usage.data?.max_engagements ?? 0 })
                : undefined
            }
          >
            {atEngagementCap ? t.planLimitReached : t.newEngagement}
          </Button>
        }
      />

      {/* Controls row */}
      <div className="flex flex-col sm:flex-row gap-2 sm:items-center mb-4 mt-2">
        <Input
          value={q}
          onChange={setQ}
          placeholder={t.searchPlaceholder}
          className="flex-1"
        />
        <select
          value={sort}
          onChange={(e) => setSort(e.target.value as SortKey)}
          className="font-mono text-[11px] bg-bg2 border border-stroke rounded px-2 py-2 text-content"
        >
          <option value="newest">{t.sortNewest}</option>
          <option value="oldest">{t.sortOldest}</option>
          <option value="name">{t.sortName}</option>
          <option value="status">{t.sortStatus}</option>
        </select>
        <select
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          className="font-mono text-[11px] bg-bg2 border border-stroke rounded px-2 py-2 text-content"
        >
          {STATUS_OPTIONS.map((s) => (
            <option key={s} value={s}>
              {t.statusLabel}: {t.statuses[s as keyof typeof t.statuses] ?? s}
            </option>
          ))}
        </select>
        {isSuperuser && (
          <select
            value={orgId}
            onChange={(e) => setOrgId(e.target.value)}
            className="font-mono text-[11px] bg-bg2 border border-stroke rounded px-2 py-2 text-content"
          >
            <option value="all">{t.companyAll}</option>
            {(orgsList.data?.orgs ?? []).map((o) => (
              <option key={o.id} value={o.id}>
                {o.name}
              </option>
            ))}
          </select>
        )}
      </div>

      {engagements.length === 0 ? (
        <Empty text={t.noMatch} />
      ) : (
        <>
          <div className="flex flex-col gap-3 sm:gap-4">
            {engagements.map((e) => {
              const badge = getStatusBadgeColors(e.status);
              return (
                <Card
                  key={e.id}
                  className="cb-grow"
                  onClick={() => navigate(`/app/engagements/${e.id}`)}
                >
                  <div className="flex justify-between items-start gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <span className="font-mono text-[12px] text-dim shrink-0">
                          #{e.display_number ?? "—"}
                        </span>
                        <span className="font-sans text-[15px] font-semibold text-content min-w-0 break-words">
                          {e.name}
                        </span>
                        <Badge color="rgb(var(--color-accent))" bg="rgb(var(--color-accent-bg))">
                          {e.pentest_type || "web"}
                        </Badge>
                      </div>
                      <div className="font-mono text-[11px] text-muted">
                        {(e.client_name || t.noClient) +
                          " · " +
                          new Date(e.created_at).toLocaleDateString()}
                      </div>
                    </div>
                    <Badge color={badge.color} bg={badge.bg}>
                      {t.statuses[e.status as keyof typeof t.statuses] ?? e.status}
                    </Badge>
                  </div>
                </Card>
              );
            })}
          </div>

          <div className="flex items-center justify-between pt-3">
            <div className="font-mono text-[10px] text-muted">
              {fmt(t.showing, { n: engagements.length, total })}
            </div>
            {hasNextPage && (
              <Button
                small
                variant="default"
                disabled={isFetchingNextPage}
                onClick={() => void fetchNextPage()}
              >
                {isFetchingNextPage ? t.loadingMore : t.loadMore}
              </Button>
            )}
          </div>
        </>
      )}
    </div>
  );
}
